import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectorRef, Input, OnChanges, SimpleChanges, HostListener, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import languagesJson from '@assets/languages.json';
import sttUnsupportedLanguagesJson from '@assets/stt-unsupported-languages.json';
import ttsUnsupportedLanguagesJson from '@assets/tts-unsupported-languages.json';

import { AuthService } from '@services/core/auth.service';
import { CallService } from '@services/core/call.service';
import { RoomSessionService } from '@services/core/room-session.service';
import { OpentokService } from '@services/core/opentok.service';
import { MeetingCoordinatorService } from '@services/core/meeting-coordinator.service';
import { LogService } from '@services/core/log.service';

import { ModalService } from '@services/support/modal.service';
import { LoaderService } from '@services/support/loader.service';
import { FlashMessageService } from '@services/support/flash-message.service';
import { UtilityService } from '@services/support/utility.service';
import { TutorialDirective } from 'app/directives/tutorial.directive';

import { fromEvent, Subscription } from 'rxjs';

import { Room } from '@models/Room';
import { FlashMessageOptions } from '@models/FlashMessageOptions';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { TicketService } from '@services/other/ticket.service';
import { UntypedFormControl } from '@angular/forms';
import { MultilanguageService } from '@services/support/multilanguage.service';
import { SoundMeter } from '@models/SoundMeter';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-control-bar',
    templateUrl: './control-bar.component.html',
    styleUrls: ['./control-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ControlBarComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild("archiveDropup", { static: true }) private archiveDropup: BsDropdownDirective;

  @ViewChild("endCallTemplate", { static: true }) private endCallTemplate: TemplateRef<any>;
  @ViewChild("startRTranslationTemplate", { static: true }) private startRTranslationTemplate: TemplateRef<any>;

  @ViewChild("installExtensionTemplate", { static: true }) private installExtensionTemplate: TemplateRef<any>;

  @Input('archiving') archiving: boolean;
  @Input('archive') archive: any;
  @Input('sidebarOpen') sidebarOpen: boolean;
  @Output() onBeforeEndcall = new EventEmitter<void>();

  pAudio: boolean = true;
  private publishAudioSub: Subscription = null;
  pVideo: boolean = true;
  private publishVideoSub: Subscription = null;
  pScreen: boolean = false;
  pScreenDisabled: boolean = true;
  screenTransitioning: boolean = false;

  sessionTime: string = "";
  sessionTimer: any;
  timerStarted: boolean = false;

  features: { [key: string]: boolean } = {
    archiving: false,
    screensharing: false,
    reactions: false,
    snapshot: false,
    arcollaboration: false,
    arplus: false
  };
  featureMessage: "Your licence doesn't contain this feature.";

  userSub: Subscription = null;
  featuresSub: Subscription = null;
  currentRoomSub: Subscription = null;

  sessionActive: boolean = false;
  sessionActiveSub: Subscription = null;

  roomAutoArchive: boolean = false;
  userArchiveAllowed: boolean = false;
  roomPeerToPeer: boolean = false;
  archiveLoading: boolean = false;
  archiveId: string;

  rightControlsAlwaysOpen: boolean = false;

  currentRoom: Room = null;
  archivePermNeeded: boolean = false;
  archivePermRequested: boolean = false;
  archivePermGranted: boolean = false;
  archivePermissionStatus: string = null;
  archivePermissionMessageId: number = null;

  audioUnavailable: boolean = false;
  audioUnavailableSub: Subscription = null;

  videoUnavailable: boolean = false;
  videoUnavailableSub: Subscription = null;

  handRaiseInProgress: boolean = false;
  trainingDisable: boolean = false;
  roomUserStatus: { training_room: boolean, session_active: boolean, user_status: string, pinning_enabled: boolean } = null;
  private roomUserStatusSub: Subscription = null;

  someoneElsesPersonalRoom: boolean = false;
  personalRoom: boolean = true;
  expertUser: boolean = true;
  meetingRoom: boolean = true;
  showEndMeetingForAll: boolean = true;

  selectedAudioDeviceId: string = null;
  selectedAudioDeviceSub: Subscription = null;
  selectedVideoDeviceId: string = null;
  selectedVideoDeviceSub: Subscription = null;
  devicesChangedSub: Subscription = null;

  mandatoryTicket: boolean = false;
  mandatoryTicketSub: Subscription = null;

  audioDevices: any = [];
  videoDevices: any = [];

  removeMeetingLink = new UntypedFormControl(false)
  addOnsSub: Subscription = null;
  invalidateMeetingLink = false;

  sttLanguages: { languageCode: string, displayName: string, disabled: boolean }[] = [];
  ttsLanguages: { languageCode: string, displayName: string, disabled: boolean }[] = [];

  selectedLanguageCode: string = null;
  selectedLanguageCode2: string = null;
  languageSub: Subscription = null;
  languageSub2: Subscription = null;

  currentPublisherStreamId: string = null;
  meetingStateSub: Subscription = null;

  rtranslationDisabled: boolean = false;
  rtranslationStarted: boolean = false;
  translationTransitioning: boolean = false;
  rTranslationAvailable: boolean = false;
  private lastTranslationGateSignature: string = null;

  get canStartTranslation(): boolean {
    return !!(
      this.currentPublisherStreamId
      && this.opentokService.hasActiveAudioSource()
      && this.selectedLanguageCode
      && this.selectedLanguageCode2
      && !this.translationTransitioning
    );
  }

  streamIfForTranslate: string = null;

  soundMeterSub: Subscription = null;

  constructor(
    private authService: AuthService,
    private callService: CallService,
    private roomSessionService: RoomSessionService,
    private loaderService: LoaderService,
    private opentokService: OpentokService,
    private meetingCoordinator: MeetingCoordinatorService,
    private logService: LogService,
    private changeDetector: ChangeDetectorRef,
    private flashMessageService: FlashMessageService,
    private utilityService: UtilityService,
    private modalService: ModalService,
    private ticketService: TicketService,
    private multilanguageService: MultilanguageService
  ) { }

  private debugAudioLog(step: string, data: any = {}) {
    if (environment.production) {
      return;
    }

    console.log("[OT-AUDIO][CONTROL-BAR]", step, {
      roomId: this.roomSessionService.currentRoomId,
      vsSessionId: this.roomSessionService.currentSessionId,
      userId: this.authService.currentUser?.id,
      currentPublishAudio: this.pAudio,
      audioUnavailable: this.audioUnavailable,
      trainingDisable: this.trainingDisable,
      sessionActive: this.sessionActive,
      selectedAudioPresent: !!this.selectedAudioDeviceId,
      ...data
    });
  }

  ngOnInit() {
    this.sttLanguages = languagesJson.languages.map(lang => {
      if (sttUnsupportedLanguagesJson.languages.find(alang => alang.languageCode === lang.languageCode)) {
        return { languageCode: lang.languageCode, displayName: lang.displayName, disabled: true };
      } else {
        return { languageCode: lang.languageCode, displayName: lang.displayName, disabled: false };
      }
    });
    this.ttsLanguages = languagesJson.languages.map(lang => {
      if (ttsUnsupportedLanguagesJson.languages.find(alang => alang.languageCode === lang.languageCode)) {
        return { languageCode: lang.languageCode, displayName: lang.displayName, disabled: true };
      } else {
        return { languageCode: lang.languageCode, displayName: lang.displayName, disabled: false };
      }
    });

    this.audioUnavailableSub = this.opentokService.selectedAudioDevice.subscribe(ad => {
      this.audioUnavailable = !ad;
      this.refreshTranslationButtonGate('selected-audio-availability');
      this.changeDetector.detectChanges();
    });
    this.videoUnavailableSub = this.opentokService.selectedVideoDevice.subscribe(vd => {
      this.videoUnavailable = !vd;
    });

    this.publishAudioSub = this.opentokService.publishAudio.subscribe(publishAudio => {
      this.pAudio = publishAudio;
      this.refreshTranslationButtonGate('publish-audio-state');
      this.changeDetector.detectChanges();
    });
    this.publishVideoSub = this.opentokService.publishVideo.subscribe(publishVideo => {
      this.pVideo = publishVideo;
      this.changeDetector.detectChanges();
    });
    this.meetingStateSub = this.meetingCoordinator.state$.subscribe(state => {
      this.currentPublisherStreamId = state.camera.streamId;
      this.screenTransitioning = state.screen.phase === 'starting' || state.screen.phase === 'stopping';
      this.pScreen = state.screen.phase === 'active' || state.screen.phase === 'stopping';
      this.pScreenDisabled = !state.screen.available && !this.pScreen;
      this.translationTransitioning = state.translation.phase === 'starting' || state.translation.phase === 'stopping';
      this.rtranslationStarted = state.translation.desired || state.translation.phase === 'active' || state.translation.phase === 'recovering';
      this.refreshTranslationButtonGate('meeting-state');
      this.changeDetector.detectChanges();
    });

    this.sessionActiveSub = this.roomSessionService.sessionActive.subscribe(active =>{
      const prev = this.sessionActive;
      this.sessionActive = active;
      if (prev !== active) {
        this.debugAudioLog("session-active-changed", {
          prev,
          next: active,
          rtranslationStarted: this.rtranslationStarted,
          pScreen: this.pScreen,
          currentPublisherStreamId: this.currentPublisherStreamId
        });
      }

      if (this.sessionActive) {
        if (this.soundMeterSub) { this.soundMeterSub.unsubscribe() }
        this.soundMeterSub = null
      } else {
        this.startSoundMeter()
      }
    });

    this.languageSub = this.multilanguageService.translationLanguage.subscribe(l => {
      if (this.sttLanguages.find(lang => lang.languageCode === l && lang.disabled)) {
        this.selectedLanguageCode = "en";
      } else {
        this.selectedLanguageCode = l;
      }
    });
    this.languageSub2 = this.multilanguageService.translationLanguage2.subscribe(l => {
      if (this.ttsLanguages.find(lang => lang.languageCode === l && lang.disabled)) {
        this.selectedLanguageCode2 = "en";
      } else {
        this.selectedLanguageCode2 = l;
      }
    });

    this.featuresSub = this.authService.features.subscribe(features => {
      this.features = features;
    });

    this.addOnsSub = this.authService.addOns.subscribe(addOns => { 
      this.invalidateMeetingLink = addOns.invalidatemeetinglink ? true : false;
      this.removeMeetingLink.patchValue(this.invalidateMeetingLink);
      this.rTranslationAvailable = addOns.realtimetranslation ? true : false;
      this.refreshTranslationButtonGate('translation-addon');
     });

    this.userSub = this.authService.user.subscribe(user => {
      this.userArchiveAllowed = user ? user.allow_archiving : false;
      this.personalRoom = user ? user.personal_room === this.callService.currentRoomId : false;
      this.expertUser = user?.role === "expert";
    });

    this.roomUserStatusSub = this.roomSessionService.roomUserStatus.subscribe(roomUserStatus => {
      this.roomUserStatus = roomUserStatus;
      this.trainingDisable = roomUserStatus.training_room ? (roomUserStatus.user_status !== "master" && roomUserStatus.user_status !== "publishing") : false;
    });

    this.currentRoomSub = this.roomSessionService.currentRoom.subscribe(room => {
      this.currentRoom = room;

      if (room) {
        this.roomAutoArchive = room.room_data.session.auto_archive;
        this.roomPeerToPeer = room.room_data.session.peer_to_peer;
        this.refreshTranslationButtonGate('room-peer-mode');

        this.archivePermNeeded = room.room_data.archive_perm_needed ? true : false;
        this.archivePermRequested = room.room_data.archive_perm_requested ? true : false;
        this.archivePermGranted = room.room_data.archive_perm_granted ? true : false;
        this.meetingRoom = room.room_data.meeting_room ? true : false;
        this.showEndMeetingForAll = (room.room_data.training_room ? false : true) && this.authService.isAddOnAvailable("endmeetingforall") && !room.room_data.personal_room;

        const permissionStatus = room.room_data.requests && room.room_data.requests.archive_permission ? room.room_data.requests.archive_permission.users[this.authService.currentUser.id] : null;
        if (permissionStatus === "needed" && this.archivePermissionStatus !== "needed") {
          this.showArchiveRequestFlashMessage(room);
        }
        if (this.archivePermissionMessageId && permissionStatus !== "needed") {
          this.flashMessageService.hide(this.archivePermissionMessageId);
          this.archivePermissionMessageId = null;
        }
        this.archivePermissionStatus = permissionStatus;
      }

      if (room && room.room_data.session.start_time && !this.timerStarted) {
        this.timerStarted = true;
        this.roomSessionService.getSessionStartTime(room.room_data.session.start_time).then(time => {

          // If something went wrong, show nothing
          if ( time < 36000 && time > -3) {
            // If error small, change it to 0
            if (time < 0) { time = 0 }

            // Start timer
            this.sessionTimer = setInterval(() => {
              if (time > 3599) {
                this.sessionTime = ("0" + Math.floor(time / 3600)).slice(-2) + ":" + ("0" + Math.floor((time / 60) % 60)).slice(-2) + ":" + ("0" + time % 60).slice(-2);
              } else {
                this.sessionTime = ("0" + Math.floor(time / 60)).slice(-2) + ":" + ("0" + time % 60).slice(-2);
              }
              time += 1;
            }, 1000);
          }
        });
      }
    });

    this.selectedAudioDeviceSub = this.opentokService.selectedAudioDevice.subscribe(ad => {
      this.selectedAudioDeviceId = ad;
      this.refreshTranslationButtonGate('selected-audio-device');
      this.changeDetector.detectChanges();
    });
    this.selectedVideoDeviceSub = this.opentokService.selectedVideoDevice.subscribe(vd => {
      this.selectedVideoDeviceId = vd;
    });

    this.getUserMedia();
    this.devicesChangedSub = fromEvent(navigator.mediaDevices, 'devicechange')
    .subscribe(() => this.getUserMedia());  
    
    this.mandatoryTicketSub = this.ticketService.mandatoryTicket
    .subscribe(active => {
      this.mandatoryTicket = active;
    });
  }

  async startSoundMeter() {
    try {
      const ctx = new AudioContext()
      const smeter = new SoundMeter(ctx)
      const currentStream = await OT.getUserMedia({ videoSource: null })
      if (this.sessionActive) {
        return
      }

      this.soundMeterSub = smeter.connectToSource(currentStream, true)
        .subscribe(n => n)
    } catch (error) {
      console.log(error)
    }
  }

  onToggleTranslate() {
    this.refreshTranslationButtonGate('translation-click');
    if (!environment.production) {
      console.log('[OT-AUDIO][CONTROL-BAR][TRANSLATION-CLICK]', JSON.stringify(this.getTranslationGateSnapshot()));
    }
    this.debugAudioLog("toggle-translate-clicked", {
      sessionActive: this.sessionActive,
      roomPeerToPeer: this.roomPeerToPeer,
      rtranslationStarted: this.rtranslationStarted,
      pScreen: this.pScreen,
      currentPublisherStreamId: this.currentPublisherStreamId,
      rtranslationDisabled: this.rtranslationDisabled,
      activeAudioSource: this.opentokService.hasActiveAudioSource(),
      gateSnapshot: this.getTranslationGateSnapshot()
    });

    if (!this.sessionActive) {
      // No need to show message
      return;
    }

    if (!this.rTranslationAvailable || this.rtranslationDisabled) {
      return;
    }

    if (this.roomPeerToPeer) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.P2P_NO_RTRANS', { cssClass: 'alert-danger', timeout: 4000 });
      return;
    }

    if (this.translationTransitioning) {
      return;
    }

    if (this.rtranslationStarted) {
      this.debugAudioLog("stop-translate-requested");
      this.meetingCoordinator.stopTranslation().catch(error => {
        this.debugAudioLog("stop-translate-failed", { errorMessage: error?.message });
      });
    } else {
      this.logTranslationModalGate('modal-open');
      const modalId = this.modalService.show({
        template: this.startRTranslationTemplate,
        context: {
          dataModel: {  },
          callbacks: {
            no: () => {
              this.modalService.hide(modalId);
            },
            yes: () => {
              this.logTranslationModalGate('start-click');
              this.modalService.hide(modalId);

              // The microphone has one stable owner: the camera publication.
              const streamId = this.opentokService.getActiveAudioStreamId();
              if (!streamId) {
                this.debugAudioLog("start-translate-aborted-no-audio-stream");
                return;
              }
              this.debugAudioLog("start-translate-requested", {
                streamId,
                pScreen: this.pScreen,
                fromLang: this.selectedLanguageCode,
                toLang: this.selectedLanguageCode2
              });
              this.loaderService.show()
              this.meetingCoordinator.startTranslation(this.selectedLanguageCode, this.selectedLanguageCode2)
                .then(() => {
                  this.debugAudioLog("start-translate-result", {
                    streamId,
                    succeeded: true,
                    currentPublisherStreamIdNow: this.currentPublisherStreamId
                  });
                })
                .catch(error => {
                  this.debugAudioLog("start-translate-failed", {
                    streamId,
                    errorName: error?.name,
                    errorMessage: error?.message
                  });
                  this.flashMessageService.show(error?.message || 'Realtime translation failed.');
                })
                .finally(() => this.loaderService.hide())
            }
          }
        }
      });
    }
  }

  private refreshTranslationButtonGate(reason: string) {
    const state = this.meetingCoordinator?.snapshot;
    if (!state) {
      return;
    }

    const activeAudioSource = this.opentokService.hasActiveAudioSource();
    // Translation depends on a stable primary stream + live microphone source,
    // not on camera hardware or the mutable stream.hasAudio mute flag.
    this.rtranslationDisabled = this.translationTransitioning
      || (!this.rtranslationStarted && (!state.camera.streamId || !activeAudioSource));

    if (!environment.production) {
      const snapshot = this.getTranslationGateSnapshot();
      const signature = JSON.stringify(snapshot);
      if (signature !== this.lastTranslationGateSignature) {
        this.lastTranslationGateSignature = signature;
        console.log('[OT-AUDIO][CONTROL-BAR][TRANSLATION-GATE]', JSON.stringify({
          reason,
          ...snapshot
        }));
      }
    }
  }

  private getTranslationGateSnapshot() {
    const state = this.meetingCoordinator?.snapshot;
    const activeAudioSource = this.opentokService.hasActiveAudioSource();
    const buttonDisabled = !this.rTranslationAvailable
      || this.rtranslationDisabled
      || this.roomPeerToPeer
      || this.translationTransitioning;
    return {
      disabled: buttonDisabled,
      mediaGateDisabled: this.rtranslationDisabled,
      blockingReasons: [
        !this.rTranslationAvailable ? 'translation-addon-unavailable' : null,
        this.roomPeerToPeer ? 'peer-to-peer-room' : null,
        this.translationTransitioning ? 'translation-transitioning' : null,
        !this.rtranslationStarted && !state?.camera?.streamId ? 'primary-stream-missing' : null,
        !this.rtranslationStarted && !activeAudioSource ? 'audio-source-missing' : null
      ].filter(Boolean),
      sessionPhase: state?.context?.phase || null,
      cameraPhase: state?.camera?.phase || null,
      primaryStreamId: state?.camera?.streamId || null,
      streamHasAudio: state?.camera?.hasAudio || false,
      streamHasVideo: state?.camera?.hasVideo || false,
      activeAudioSource,
      publishAudio: this.pAudio,
      selectedAudioPresent: !!this.selectedAudioDeviceId,
      audioUnavailable: this.audioUnavailable,
      translationPhase: state?.translation?.phase || null,
      translationDesired: state?.translation?.desired || false,
      translationTransitioning: this.translationTransitioning
    };
  }

  onLangChange(language: any) {
    this.multilanguageService.changePreferedTranslationLang(language.languageCode)
    this.logTranslationModalGate('source-language-change');
  }

  onLangChange2(language: any) {
    this.multilanguageService.changePreferedTranslationLang2(language.languageCode)
    this.logTranslationModalGate('target-language-change');
  }

  private logTranslationModalGate(reason: string) {
    if (environment.production) {
      return;
    }
    console.log('[OT-AUDIO][CONTROL-BAR][TRANSLATION-MODAL-GATE]', JSON.stringify({
      reason,
      disabled: !this.canStartTranslation,
      primaryStreamId: this.currentPublisherStreamId,
      activeAudioSource: this.opentokService.hasActiveAudioSource(),
      sourceLanguage: this.selectedLanguageCode,
      targetLanguage: this.selectedLanguageCode2,
      translationTransitioning: this.translationTransitioning
    }));
  }

  async getUserMedia() {
    const devices = await (new Promise<OT.Device[]>((resolve, reject) => {
      OT.getDevices((error, devices) => {
        if (error) {
          reject(error);
        } else {
          resolve(devices);
        }
      });
    }));

    //Implemented due to Firefox Android device list label issue
    devices.filter(d => d.kind === "audioInput").forEach((d, i) => {
      d.label = !d.label ? `Device ${i}` : d.label
    })
    devices.filter(d => d.kind === "videoInput").forEach((d, i) => {
      d.label = !d.label ? `Device ${i}` : d.label
    })
    this.audioDevices = devices.filter(d => d.kind === "audioInput");
    this.videoDevices = devices.filter(d => d.kind === "videoInput");
  }

  onAudioDeviceSelected(device) {
    this.debugAudioLog("audio-device-selected", {
      deviceIdPresent: !!device?.deviceId,
      label: device?.label || null,
      rtranslationStarted: this.rtranslationStarted,
      pScreen: this.pScreen
    });
    this.opentokService.changeAudioDevice(device.deviceId);
  }

  onVideoDeviceSelected(device) {
    this.opentokService.changeVideoDevice(device.deviceId);
  }

  showArchiveRequestFlashMessage(room: any) {
    const flashMessageOptions: FlashMessageOptions = {
      cssClass: "alert-info", noTimeout: true, closeOnClick: false, showCloseBtn: false,
      buttons: [
        {
          title: "APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.DENY",
          cssClass: "btn-danger",
          callback: () => {
            this.opentokService.denyArchive();
            this.flashMessageService.hide(this.archivePermissionMessageId);
            this.archivePermissionMessageId = null;
          }
        },
        {
          title: "APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ALLOW",
          cssClass: "btn-success",
          callback: () => {
            this.opentokService.allowArchive();
            this.flashMessageService.hide(this.archivePermissionMessageId);
            this.archivePermissionMessageId = null;
          }
        },
      ]
    };
    const requesterName = room.room_data.requests.archive_permission.requester ? room.room_data.requests.archive_permission.requester.name : null;
    if (requesterName) {
      this.flashMessageService.showTranslatedWithData('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_REQUESTED_WITH_NAME', { name: requesterName }, flashMessageOptions)
        .then(id => { this.archivePermissionMessageId = id });
    } else {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_REQUESTED', flashMessageOptions)
        .then(id => { this.archivePermissionMessageId = id });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.archiving) {
      this.archiving = changes.archiving.currentValue;
    }
    if (changes.archive) {
      this.archive = changes.archive.currentValue;
    }
    if (changes.sidebarOpen) {
      this.sidebarOpen = changes.sidebarOpen.currentValue;
      this.rightControlsAlwaysOpen = false;
    }
  }

  ngOnDestroy() {
    if (this.sessionTimer) { clearInterval(this.sessionTimer) }
    if (this.sessionActiveSub) { this.sessionActiveSub.unsubscribe() }
    if (this.audioUnavailableSub) { this.audioUnavailableSub.unsubscribe() }
    if (this.videoUnavailableSub) { this.videoUnavailableSub.unsubscribe() }
    if (this.publishAudioSub) { this.publishAudioSub.unsubscribe() }
    if (this.publishVideoSub) { this.publishVideoSub.unsubscribe() }
    if (this.currentRoomSub) { this.currentRoomSub.unsubscribe() }
    if (this.featuresSub) { this.featuresSub.unsubscribe() }
    if (this.userSub) { this.userSub.unsubscribe() }
    if (this.roomUserStatusSub) { this.roomUserStatusSub.unsubscribe() }
    if (this.selectedAudioDeviceSub) { this.selectedAudioDeviceSub.unsubscribe() }
    if (this.selectedVideoDeviceSub) { this.selectedVideoDeviceSub.unsubscribe() }
    if (this.devicesChangedSub) { this.devicesChangedSub.unsubscribe() }
    if (this.mandatoryTicketSub) { this.mandatoryTicketSub.unsubscribe() }
    if (this.addOnsSub) { this.addOnsSub.unsubscribe() }
    if (this.languageSub) { this.languageSub.unsubscribe() }
    if (this.languageSub2) { this.languageSub2.unsubscribe() }
    if (this.meetingStateSub) { this.meetingStateSub.unsubscribe() }
    if (this.soundMeterSub) { this.soundMeterSub.unsubscribe() }

    this.flashMessageService.hide(this.archivePermissionMessageId);
    this.archivePermissionMessageId = null;
  }

  onEndCall(endForAll: boolean) {
    this.ticketService.mandatoryTicketSource.next(false)
    const modalId = this.modalService.show({
      template: this.endCallTemplate,
      context: {
        dataModel: { personalRoom: this.personalRoom, showEndMeetingForAll: this.showEndMeetingForAll, meetingRoom: this.meetingRoom, endForAll: endForAll },
        callbacks: {
          no: () =>{ 
            this.modalService.hide(modalId)
            this.ticketService.mandatoryTicketSource.next(true)
          },
          yes: () => {
            this.onBeforeEndcall.emit();

            this.modalService.hide(modalId);
            this.loaderService.show();

            setTimeout(() => {
              this.removeMeetingLink.patchValue(!endForAll || !this.meetingRoom ? false : this.removeMeetingLink.value)
              const removeMeetingLinkParam = this.removeMeetingLink.value
              this.callService.endCall(endForAll, removeMeetingLinkParam)
                .catch(error => {
                  console.log(error);
                  this.callService.simulateEndcallForFrontend(this.roomSessionService.currentRoomId, this.roomSessionService.currentSessionId, "endcall");
                })
                .finally(() => this.loaderService.hide());
            }, 100);
          }
        }
      }
    });
  }

  onToggleAudio() {
    this.debugAudioLog("toggle-audio-clicked");
    if (this.trainingDisable) {
      this.debugAudioLog("toggle-audio-blocked-training-disabled");
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }
    if (this.audioUnavailable) {
      this.debugAudioLog("toggle-audio-blocked-audio-unavailable");
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.CAM_MIC_CANT_ACCESS');
      return;
    }
    this.meetingCoordinator.toggleAudio().catch(error => {
      this.debugAudioLog("toggle-audio-failed", { errorMessage: error?.message });
    });
  }

  onToggleVideo() {
    if (this.trainingDisable) {
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }
    if (this.videoUnavailable) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.CAM_MIC_CANT_ACCESS');
      return;
    }
    this.meetingCoordinator.toggleVideo().catch(error => {
      this.debugAudioLog("toggle-video-failed", { errorMessage: error?.message });
    });
  }

  onStartArchive(event) {
    if (this.roomAutoArchive || this.archiveLoading) {
      // No need to show message
      return;
    }
    if (!this.features.archiving) {
      this.flashMessageService.showTranslated('APP.SHARED.NO_FEATURE', { timeout: 6000 });
      return;
    }
    if (!this.userArchiveAllowed) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_NO_PERMISSION', { timeout: 6000 });
      return;
    }
    if (this.roomPeerToPeer) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.P2P_NO_ARCHIVE', { timeout: 6000 });
      return;
    }
    if (this.trainingDisable) {
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }

    if (this.archivePermNeeded) {
      if (!this.archivePermGranted) {
        if (this.archivePermRequested) {
          this.archiveLoading = true;
          this.opentokService.cancelArchiveRequest().finally(() => {
            this.archiveLoading = false;
          });
        } else {
          event.stopPropagation();
          this.archiveDropup.toggle(true);
        }
      }
    } else {
      this.archiveLoading = true;
      this.opentokService.startArchive().finally(() => {
        this.archiveLoading = false;
      });
    }
    /*if (this.currentRoom && this.currentRoom.room_data.archive_perm_needed) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_PERMISSION_REQUESTED', { cssClass: "alert-info" });
    }*/
  }

  onAskRecordPermission() {
    if (this.roomAutoArchive) {
      // No need to show message
      return;
    }
    if (!this.features.archiving) {
      this.flashMessageService.showTranslated('APP.SHARED.NO_FEATURE', { timeout: 6000 });
      return;
    }
    if (!this.userArchiveAllowed) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_NO_PERMISSION', { timeout: 6000 });
      return;
    }
    if (this.roomPeerToPeer) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.P2P_NO_ARCHIVE', { timeout: 6000 });
      return;
    }
    if (this.trainingDisable) {
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }

    this.archiveLoading = true;
    this.opentokService.startArchive().finally(() => {
      this.archiveLoading = false;
    });
  }

  onStopArchive() {
    if (!this.userArchiveAllowed) {
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.ARCHIVE_NO_PERMISSION', { timeout: 6000 });
      return;
    }
    if (this.trainingDisable) {
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }

    this.archiveLoading = true;
    this.opentokService.stopArchive(this.archive.id).finally(() => {
      this.archiveLoading = false;
    });
  }

  onToggleScreenSharing() {
    this.debugAudioLog("toggle-screen-share-clicked", {
      pScreen: this.pScreen,
      rtranslationStarted: this.rtranslationStarted,
      currentPublisherStreamId: this.currentPublisherStreamId
    });
    if (this.screenTransitioning) {
      return;
    }
    if (this.pScreen) {
      this.debugAudioLog("end-screen-share-requested");
      this.meetingCoordinator.setScreenSharing(false).catch(error => {
        this.debugAudioLog("end-screen-share-failed", { errorMessage: error?.message });
      });
    } else if (!this.features.screensharing) {
      this.flashMessageService.showTranslated('APP.SHARED.NO_FEATURE');
    } else if (this.trainingDisable) {
      if (this.sessionActive) {
        this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      }
    } else if (!this.pScreenDisabled) {
      this.opentokService.checkScreenSharingCapability()
      .then(response => {
        const _response: any = response;

        if (!response.supported || _response.extensionRegistered === false) {
          const browserName = this.utilityService.getBrowserParser().getBrowserName();
          const browserVersion = parseInt(this.utilityService.getBrowserParser().getBrowserVersion().split('.')[0]);
          if ((browserName === "Firefox" && browserVersion < 52) || (browserName === "Chrome" && browserVersion < 72)) {
            this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.SCREEN_SHARE_UPDATE');
          } else {
            this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.SCREEN_SHARE_NOT_SUPPORTED');
          }
        } else if (this.utilityService.getBrowserParser().is("Safari")) {
          this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.SCREEN_SHARE_USE_OTHER');
        } else {
          this.startScreenSharing();
        }
      });
    }
  }

  startScreenSharing() {
    this.debugAudioLog("start-screen-share-requested", {
      rtranslationStarted: this.rtranslationStarted,
      currentPublisherStreamId: this.currentPublisherStreamId,
      // Screen is video-only; translation stays on camera streamId (no restart).
      expectRtranslationRestart: false
    });
    this.meetingCoordinator.setScreenSharing(true)
    .then(() => {
      this.debugAudioLog("start-screen-share-succeeded", {
        currentPublisherStreamId: this.currentPublisherStreamId,
        rtranslationStarted: this.rtranslationStarted,
        rtranslationRestarted: false
      });
    })
    .catch(error => {
      this.debugAudioLog("start-screen-share-failed", {
        errorName: error?.name,
        errorMessage: error?.message
      });
      this.flashMessageService.showTranslated('APP.MAIN.ROOM.VIDEO_CHAT.CONTROL_BAR.SCREEN_SHARE_FAILED');
    })
  }

  onReactionClick() {
    if (!this.features.reactions) {
      this.flashMessageService.showTranslated('APP.SHARED.NO_FEATURE');
      return;
    }
    if (!this.sessionActive) {
      // No need to show message
      return;
    }
    if (this.trainingDisable) {
      this.flashMessageService.showTranslated('APP.SHARED.TRAINING_DISABLED');
      return;
    }
  }

  onSendReaction(reaction: string) {
    this.logService.sendReactionLog(reaction);
  }

  onReactionNext(tutorial: TutorialDirective) {
    if (this.features.arcollaboration) {
      if (this.features.arplus) {
        tutorial.next({subscriber: true, arColl: true, arPlus: true, noArPlus: true});
      } else {
        tutorial.next({subscriber: true, arColl: true, noArPlus: true});
      }
    } else {
      if (this.features.arplus) {
        tutorial.next({subscriber: true, arPlus: true, noArPlus: true});
      } else {
        if (this.features.snapshot) {
          tutorial.next({subscriber: true, snapshot: true, noSnapshot: true});
        } else {
          tutorial.next({showTutorial: true});
        }
      }
    }
  }

  onRaiseHand() {
    this.handRaiseInProgress = true;
    this.roomSessionService.raiseHand()
    .finally(() => { this.handRaiseInProgress = false; })
  }

  onLowerHand() {
    this.handRaiseInProgress = true;
    this.roomSessionService.lowerHand()
    .finally(() => { this.handRaiseInProgress = false; })
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.archiveDropup.isOpen) {
      this.archiveDropup.hide();
    }
  }
}
